/**
 * Upper-cases the first character, leaving the rest as given.
 * @param {string} str - The text to capitalize
 * @return {string} The text with its first character upper-cased
 */
function capitalizeFirstLetter(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Splits a PascalCase identifier into space-separated words.
 * @param {string} str - The PascalCase text, e.g. "DivisionOrder"
 * @return {string} The words separated by spaces, e.g. "Division Order"
 */
const pascaleCaseToTitleCase = (str: string): string => {
  return str.replace(/([A-Z])/g, ' $1').trim();
};

/**
 * Ends a sentence with a period, unless its last word (e.g. "Inc.") already
 * supplies one.
 * @param {string} text - The sentence, possibly with trailing whitespace
 * @return {string} The trimmed sentence ending in exactly one period
 */
const endSentence = (text: string): string => {
  const trimmed = text.trimEnd();
  return trimmed.endsWith('.') ? trimmed : `${trimmed}.`;
};

/**
 * Joins clauses into one natural phrase: "a" / "a and b" / "a, b, and c".
 *
 * @param {string[]} clauses - The clauses, in order.
 * @return {string} The joined phrase ('' when empty).
 */
function joinClauses(clauses: string[]): string {
  if (clauses.length <= 1) {
    return clauses[0] ?? '';
  }
  if (clauses.length === 2) {
    return `${clauses[0]} and ${clauses[1]}`;
  }
  const head = clauses.slice(0, -1).join(', ');
  return `${head}, and ${clauses[clauses.length - 1]}`;
}

/**
 * Picks the singular or plural wording for a count (exactly 1 is singular).
 *
 * @param {number} count - The count the wording agrees with.
 * @param {string} singular - The wording for exactly one.
 * @param {string} plural - The wording for any other count.
 * @return {string} The matching wording.
 */
function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

export {
  capitalizeFirstLetter,
  endSentence,
  joinClauses,
  pascaleCaseToTitleCase,
  pluralize,
};
